import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormModal } from "@/components/form-modal";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

const createOrgSchema = z.object({
  name: z.string().min(1, "Organisation name is required"),
  username: z.string().min(1, "Owner username is required"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type CreateOrgFormValues = z.infer<typeof createOrgSchema>;

async function createOrganisation(values: CreateOrgFormValues) {
  const response = await fetch("/api/organisations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(values),
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    
    // Handle field-specific errors (validation or unique constraint)
    if (response.status === 400 && errorData.errors) {
      throw { fields: errorData.errors };
    }
    
    if (response.status === 409) {
      // Handle unique constraint violations
      if (errorData.message.includes('Organisation name')) {
        throw { field: 'name', message: 'This organisation name is already taken' };
      }
      if (errorData.message.includes('Username')) {
        throw { field: 'username', message: 'This username is already taken' };
      }
    }
    
    // Handle other errors with a user-friendly message
    throw new Error(errorData.message || "Unable to create organisation. Please try again later.");
  }
  
  return response.json();
}

interface CreateOrganisationModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function CreateOrganisationModal({ isOpen, onOpenChange }: CreateOrganisationModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<CreateOrgFormValues>({
    resolver: zodResolver(createOrgSchema),
    defaultValues: {
      name: "",
      username: "",
      password: "",
    },
  });

  // Clear form errors when modal is closed
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.clearErrors();
    }
    onOpenChange(open);
  };

  const mutation = useMutation({
    mutationFn: createOrganisation,
    onSuccess: () => {
      toast({ 
        title: "Success", 
        description: "Organisation created successfully." 
      });
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      form.clearErrors();
      form.reset();
      onOpenChange(false);
    },
    onError: (error: Error | { field?: string; message?: string; fields?: Record<string, string> }) => {
      if ('field' in error && error.field && error.message) {
        // Single field error
        form.setError(error.field as keyof CreateOrgFormValues, {
          type: 'manual',
          message: error.message
        });
      } else if ('fields' in error && error.fields) {
        // Multiple field errors
        Object.entries(error.fields).forEach(([field, message]) => {
          form.setError(field as keyof CreateOrgFormValues, {
            type: 'manual',
            message: message as string
          });
        });
      } else {
        // Generic error
        const errorMessage = error instanceof Error ? error.message : 'An error occurred';
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    },
  });

  function onSubmit(values: CreateOrgFormValues) {
    form.clearErrors();
    mutation.mutate(values);
  }

  return (
    <FormModal
      open={isOpen}
      onOpenChange={handleOpenChange}
      title="Create New Organisation"
      form={form}
      onSubmit={onSubmit}
      isLoading={mutation.isPending}
      submitLabel={mutation.isPending ? "Creating..." : "Create"}
      width="md"
    >
      <div className="text-sm text-muted-foreground mb-4">
        This will create a new organisation and an owner account for it.
      </div>
      
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Organisation Name</FormLabel>
            <FormControl>
              <Input 
                placeholder="Acme Inc." 
                {...field} 
                autoComplete="off"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="username"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Owner Username</FormLabel>
            <FormControl>
              <Input 
                placeholder="admin" 
                {...field} 
                autoComplete="off"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="password"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Owner Password</FormLabel>
            <FormControl>
              <Input 
                type="password" 
                placeholder="********" 
                {...field} 
                autoComplete="new-password"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FormModal>
  );
} 